import { useState } from 'react';
import { Shield, Copy, CheckCircle2 } from 'lucide-react';

export default function HashGenerator() {
  const [input, setInput] = useState('');
  const [hashes, setHashes] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState<string | null>(null);

  const generateHashes = async (text: string) => {
    if (!text) {
      setHashes({});
      return;
    }

    const encoder = new TextEncoder();
    const data = encoder.encode(text);

    try {
      // SHA-256
      const sha256Buffer = await crypto.subtle.digest('SHA-256', data);
      const sha256Hash = Array.from(new Uint8Array(sha256Buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // SHA-384
      const sha384Buffer = await crypto.subtle.digest('SHA-384', data);
      const sha384Hash = Array.from(new Uint8Array(sha384Buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      // SHA-512
      const sha512Buffer = await crypto.subtle.digest('SHA-512', data);
      const sha512Hash = Array.from(new Uint8Array(sha512Buffer))
        .map(b => b.toString(16).padStart(2, '0'))
        .join('');

      setHashes({
        'SHA-256': sha256Hash,
        'SHA-384': sha384Hash,
        'SHA-512': sha512Hash,
      });
    } catch (error) {
      console.error('Hash generation failed:', error);
    }
  };

  const copyToClipboard = (hash: string, type: string) => {
    navigator.clipboard.writeText(hash);
    setCopied(type);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="bg-gradient-to-br from-gray-50 to-white dark:from-dark-bg dark:to-dark-surface rounded-2xl p-6 border-2 border-gray-200 dark:border-dark-border shadow-lg">
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-gradient-to-br from-coral-500 to-teal-500 rounded-xl shadow-lg">
          <Shield className="w-5 h-5 text-white" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900 dark:text-dark-text">Hash Generator</h2>
          <p className="text-sm text-gray-500 dark:text-dark-muted">Generate SHA-256, SHA-384, SHA-512 hashes</p>
        </div>
      </div>

      <div className="mb-4">
        <label className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-2 block">
          Input Text
        </label>
        <textarea
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            generateHashes(e.target.value);
          }}
          placeholder="Enter text to hash..."
          className="w-full h-32 p-3 border border-gray-300 dark:border-gray-600 rounded-lg font-mono text-sm bg-white dark:bg-dark-bg text-gray-900 dark:text-dark-text focus:outline-none focus:ring-2 focus:ring-coral-400 focus:border-transparent resize-none transition-all"
        />
      </div>

      {Object.keys(hashes).length > 0 && (
        <div className="space-y-3">
          {Object.entries(hashes).map(([algorithm, hash]) => (
            <div key={algorithm} className="bg-white dark:bg-dark-surface border border-gray-200 dark:border-dark-border rounded-lg p-4">
              <div className="flex items-center justify-between mb-2">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-dark-text">{algorithm}</h3>
                <button
                  onClick={() => copyToClipboard(hash, algorithm)}
                  className="p-1.5 hover:bg-gray-100 dark:hover:bg-dark-border rounded transition-colors"
                  title={`Copy ${algorithm}`}
                >
                  {copied === algorithm ? (
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                  ) : (
                    <Copy className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  )}
                </button>
              </div>
              <pre className="text-xs font-mono bg-gray-50 dark:bg-dark-bg p-3 rounded overflow-x-auto break-all text-gray-900 dark:text-dark-text">
                {hash}
              </pre>
            </div>
          ))}
        </div>
      )}

      {!input && (
        <div className="text-center py-12 text-gray-500 dark:text-dark-muted">
          <Shield className="w-16 h-16 mx-auto mb-4 opacity-30" />
          <p className="text-lg font-semibold mb-2">No Input</p>
          <p className="text-sm">Enter text above to generate hashes</p>
        </div>
      )}
    </div>
  );
}
